import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SerializedError } from "../shared/models/errorHandlingModels";
import { ConfirmInvite, ConfirmInviteForm } from '../customer/shared/models/customerModels';
import customersApi from '../customer/shared/api/customersApi';
import { serializeError, createErrorNotification } from '../shared/utils/errorUtils';

interface ConfirmInviteRegistrationState {
  isLoading: boolean;
  isSuccess: boolean;
  formNotificationMessage?: string | null;
  showPassword: boolean;
  showConfirmPassword: boolean;
  formData?: ConfirmInviteForm | null;
}

const initialState: ConfirmInviteRegistrationState = {
  isLoading: false,
  isSuccess: false,
  formNotificationMessage: null,
  showPassword: false,
  showConfirmPassword: false,
  formData: null,
};

export const confirmInviteRegistration = createAsyncThunk(
  'confirmInviteRegistration/confirmInviteRegistration',
  async (model: ConfirmInvite, { rejectWithValue }) => {
    try {
      const result = await customersApi.confirmInvite(model);
      return result;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const confirmInviteForm = createAsyncThunk(
  'confirmInviteForm/confirmInviteForm',
  async ({ userId, token }: { userId: string, token: string }, { rejectWithValue }) => {
    try {
      const result = await customersApi.confirmInviteForm(userId, token);
      return result;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const confirmInviteRegistrationSlice = createSlice({
  name: 'confirmInviteRegistration',
  initialState,
  reducers: {
    setFormNotificationMessage: (state, action: PayloadAction<string | null>) => {
      state.formNotificationMessage = action.payload;
    },
    togglePasswordVisibility: (state) => {
      state.showPassword = !state.showPassword;
    },
    toggleConfirmPasswordVisibility: (state) => {
      state.showConfirmPassword = !state.showConfirmPassword;
    },
    showSuccess: (state) => {
      state.isLoading = false;
      state.isSuccess = true;
    },
    resetState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.formNotificationMessage = null;
      state.showPassword = false;
      state.showConfirmPassword = false;
      state.formData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(confirmInviteRegistration.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = null;
      })
      .addCase(confirmInviteRegistration.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(confirmInviteRegistration.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError).message;
      })

      .addCase(confirmInviteForm.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = null;
      })
      .addCase(confirmInviteForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.formData = action.payload;
      })
      .addCase(confirmInviteForm.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError).message;
      });
  },
});

export const {
  setFormNotificationMessage,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  showSuccess,
  resetState,
} = confirmInviteRegistrationSlice.actions;

export default confirmInviteRegistrationSlice.reducer; 